import React, {useState, useMemo, useLayoutEffect, useEffect} from "react";
import {
    Row,
    Col,
    Checkbox,
    Radio,
    Select,
    Dropdown,
    Menu,
    Divider,
    Tag,
    Tooltip,
    message
} from "antd";
import moment from "moment";
import {useObserver} from "mobx-react-lite";
import {DownloadOutlined, CheckOutlined} from "@ant-design/icons";
import locale from "antd/es/date-picker/locale/tr_TR";
import {useTranslation} from "react-i18next";
import {useStore} from "../../hooks/useStore";
import {CustomButton} from "../_defaultComponents/CustomButton/CustomButton";

import {SelectField, DateRangePicker} from "./reportPageStyles";
import {
    CityFilter,
    CompanyFilter,
    PoolFilter,
    SubCompanyFilter
} from "../_defaultComponents/FilterComponents";
import {arrayAlphabeticalSort, b64DecodeToUrl, normalizeText} from "../../utils/helpers";
import {DownloadButton} from "../_defaultComponents/DownloadButton/DownloadButton";
import {useFetch} from "../../hooks/useFetch";

export const ReportsPage = () => {
    //#region variables
    const stores = useStore();
    const {t, i18n} = useTranslation(["reportPage"]);
    const [reportType, setReportType] = useState("");
    const [prevReportType, setPrevReportType] = useState("");
    const [dateRange, setDateRange] = useState([]);
    const [orderList, setOrderList] = useState(null);
    const [hoursList, setHoursList] = useState(null);
    const [shiftsList, setShiftsList] = useState(null);
    const [poolShiftList, setPoolShiftList] = useState(null);
    const [shiftEditLogsList, setShiftEditLogsList] = useState(null);
    const [courierFeedbackList, setCourierFeedbackList] = useState(null);
    const [subCompaniesList, setSubCompaniesList] = useState(null);
    const [userReportList, setUserReportList] = useState(null);
    const [courierDedicationList, setCourierDedicationList] = useState(null);
    const [subcompanySalesList, setSubcompanySalesList] = useState(null);
    const [subcompanyContractsList, setSubcompanyContractsList] = useState(null);
    const [additionalColumns, setAdditionalColumns] = useState([]);
    const [dateOption, setDateOption] = useState(undefined);
    const [reportColumns, setReportColumns] = useState(undefined);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(undefined);
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [selectedSubCompanies, setSelectedSubCompanies] = useState([]);
    const [selectedCity, setSelectedCity] = useState(null);
    const [poolGroups, setPoolGroups] = useState([]);
    const [poolIdsShifts, setPoolIdsShifts] = useState([]);
    const [workType, setWorkType] = useState("all");
    const [activeSubCompany, setActiveSubCompany] = useState("all");
    const [contractDate, setContractDate] = useState(null);
    const [downloadReport, setDownloadReport] = useState(false);
    //#endregion

    //#region handle funcs
    useLayoutEffect(() => {
        if (prevReportType !== "deliveries" && reportType === "deliveries") {
            const deliveriesSelectedColumsText = localStorage.getItem("deliveriesSelectedColumns");
            if (Boolean(deliveriesSelectedColumsText)) {
                setSelectedColumns(JSON.parse(deliveriesSelectedColumsText));
            }
        }
    }, [reportType]);

    useEffect(() => {
        setSelectedCompany(undefined);
        setSelectedCompanies([]);
        setSelectedSubCompanies([]);
    }, [reportType]);

    const [isPoolLoading, pools] = useFetch({
        fetchFn: stores.poolStore.getPoolList.bind(null, {
            limit: 1000,
            page: 1,
            city: selectedCity
        }),
        initialCall: true,
        dependencies: [selectedCity]
    });

    const onChangeReportType = async value => {
        setOrderList(null);
        setHoursList(null);
        setShiftsList(null);
        setPoolShiftList(null);
        setCourierFeedbackList(null);
        setCourierDedicationList(null);
        setSubcompanySalesList(null);
        setSubcompanyContractsList(null);
        setPrevReportType(reportType);
        setReportType(value);
        setSubCompaniesList(null);
        setUserReportList(null);
        setSelectedCity(null);
        setPoolGroups([]);
        setPoolIdsShifts([]);
        if (value === "deliveryOrders") {
            const endDate = moment()
                .endOf("day")
                .toDate();
            const startDate = moment()
                .subtract(1, "days")
                .startOf("day")
                .toDate();
            setDateRange([startDate, endDate]);
        } else if (value === "hours") {
            const endDate = moment()
                .endOf("day")
                .toDate();
            const startDate = moment()
                .subtract(3, "days")
                .startOf("day")
                .toDate();
            setDateRange([startDate, endDate]);
        } else {
            setDateRange([]);
        }
        if (value === "deliveries" || value === "deliveryOrders") {
            const result = await stores.orderStore.getRaportColumns({reportType: value});
            setReportColumns(result);
            setSelectedColumns(result.map(r => r.key));
        } else {
            setReportColumns(null);
        }
    };
    //#endregion

    //#region requests
    const onClickOkButton = async () => {
        let href;
        let download;
        let start;
        let end;
        if (dateRange) {
            [start, end] = dateRange;
        }
        if (reportType === "hours") {
            const result = await stores.courierStore.getHoursReport({
                startDate: dateRange[0] ? new Date(start) : null,
                endDate: dateRange[1] ? new Date(end) : null,
                additionalColumns,
                dateOption: dateOption || null
            });
            await setHoursList(result);
            href = result;
            download = `${t("reportFilenames.hourList", {ns: "reportPage"})} - ${moment(
                Date()
            ).format("DD-MM-YYYY-HH-mm-ss")}.xlsx`;
        } else if (reportType === "shifts") {
            const result = await stores.courierStore.getShiftsReport({
                startDate: dateRange[0] ? new Date(start) : null,
                endDate: dateRange[1] ? new Date(end) : null
            });
            await setShiftsList(result);
            if (result?.length === 0) {
                return;
            }
            href = result;
            download = `${t("reportFilenames.shiftsList", {ns: "reportPage"})} - ${moment(
                Date()
            ).format("DD-MM-YYYY-HH-mm-ss")}.xlsx`;
        } else if (reportType === "shiftEditLogs") {
            if (!dateRange[0] || !dateRange[1]) {
                message.error("Lütfen başlangıç ve bitiş tarihlerini seçiniz");
                return;
            }
            const result = await stores.courierStore.getShiftEditLogsReport({
                period: "daily",
                startDate: dateRange[0] ? moment(start).format("YYYY-MM-DD") : null,
                endDate: dateRange[1] ? moment(end).format("YYYY-MM-DD") : null
            });
            await setShiftEditLogsList(result);
            href = result;
            download = `Kurye Vardiya Güncelleme Kayıtları Raporu - ${moment(Date()).format(
                "DD-MM-YYYY-HH-mm-ss"
            )}.xlsx`;
        } else if (reportType === "poolShiftsReport") {
            const result = await stores.poolShiftStore.poolShiftsReport({
                startDate: dateRange[0] ? moment(start).format("YYYY-MM-DD") : "",
                endDate: dateRange[1] ? moment(end).format("YYYY-MM-DD") : "",
                poolIds: poolIdsShifts,
                excel: true
            });
            const base64Data = result?.data;
            const blobUrl = await b64DecodeToUrl(base64Data);
            await setPoolShiftList(blobUrl);
            href = blobUrl;
            download = `${t("reportFilenames.poolShiftsList", {ns: "reportPage"})} - ${moment(
                Date()
            ).format("DD-MM-YYYY-HH-mm-ss")}.xlsx`;
        } else if (reportType === "dailyShifts") {
            const result = await stores.courierStore.getDailyShiftsReport();
            await setShiftsList(result);
            href = result;
            download = `${t("reportFilenames.dailyShiftsList", {ns: "reportPage"})} - ${moment(
                Date()
            ).format("DD-MM-YYYY-HH-mm-ss")}.xlsx`;
        } else if (reportType === "feedbacks") {
            const result = await stores.courierStore.getFeedbacksReport({
                startDate: dateRange[0] ? new Date(start) : null,
                endDate: dateRange[1] ? new Date(end) : null
            });
            await setCourierFeedbackList(result);
            href = result;
            download = `${t("reportFilenames.courierFeedbackList", {ns: "reportPage"})} - ${moment(
                Date()
            ).format("DD-MM-YYYY-HH-mm-ss")}.xlsx`;
        } else if (reportType === "subCompanies") {
            const result = await stores.courierStore.getSubCompaniesReport();
            await setSubCompaniesList(result);
            href = result;
            download = `${t("reportFilenames.subCompaniesList", {ns: "reportPage"})} - ${moment(
                Date()
            ).format("DD-MM-YYYY-HH-mm-ss")}.xlsx`;
        } else if (reportType === "userReport") {
            const result = await stores.userStore.getUserReport();
            await setUserReportList(result);
            href = result;
            download = `${"kullanici-raporu"}.xlsx`;
        } else if (reportType === "subcompanyContracts") {
            const result = await stores.companyStore.getSubcompanyContracts({
                active:
                    activeSubCompany === "all" || !activeSubCompany
                        ? undefined
                        : activeSubCompany === true
                        ? true
                        : false,
                workType:
                    workType === "all" || !workType
                        ? undefined
                        : workType === "pool"
                        ? "pool"
                        : "dedicated",
                contractDate: contractDate ? moment(contractDate).format("YYYY-MM-DD") : undefined
            });
            const base64Data = result?.data;
            const blobUrl = await b64DecodeToUrl(base64Data);
            await setSubcompanyContractsList(blobUrl);
            href = blobUrl;
            download = `${t("reportFilenames.subcompanyContractsList", {
                ns: "reportPage"
            })} - ${moment(Date()).format("DD-MM-YYYY-HH-mm-ss")}.xlsx`;
        } else if (reportType === "dedications") {
            const result = await stores.courierStore.getDedicationsReport({
                startDate: dateRange[0] ? new Date(start) : null,
                endDate: dateRange[1] ? new Date(end) : null
            });
            await setCourierDedicationList(result);
            href = result;
            download = `${t("reportFilenames.courierDedicationList", {
                ns: "reportPage"
            })} - ${moment(Date()).format("DD-MM-YYYY-HH-mm-ss")}.xlsx`;
        } else if (reportType === "subcompanySales") {
            if (
                !selectedCompany &&
                (selectedCity === null || selectedCity === "all") &&
                (workType === null || workType === "all")
            ) {
                message.error("En az bir filtre seçiniz");
                return;
            }
            const result = await stores.companyStore.getSubCompanySalesReport({
                companyId: selectedCompany,
                workType: workType === "all" ? undefined : workType,
                city: selectedCity === "all" ? undefined : selectedCity
            });
            await setSubcompanySalesList(result);
            href = result;
            download = `Alt Firma Satış Raporu - ${moment(Date()).format(
                "DD-MM-YYYY-HH-mm-ss"
            )}.xlsx`;
        } else {
            selectedColumns.length > 0 &&
                localStorage.setItem("deliveriesSelectedColumns", JSON.stringify(selectedColumns));
            const columnList =
                selectedColumns.length > 0
                    ? [...selectedColumns]
                    : [...reportColumns?.map(column => column.key)];
            let result = undefined;
            if (reportType === "deliveryOrders") {
                result = await stores.orderStore.getDeliveryOrdersReport({
                    startDate: dateRange[0]
                        ? new Date(start)
                        : new Date(moment().subtract(1, "days")),
                    endDate: dateRange[1] ? new Date(end) : new Date(),
                    selectedColumns: columnList,
                    companies: selectedCompanies,
                    poolIds:
                        poolGroups.length === 0 ? pools?.data?.map(pool => pool._id) : poolGroups,
                    workType: workType,
                    city: selectedCity
                });
            } else {
                result = await stores.orderStore.getOrderReport({
                    startDate: dateRange[0]
                        ? new Date(start)
                        : new Date(moment().subtract(15, "days")),
                    endDate: dateRange[1] ? new Date(end) : new Date(),
                    selectedColumns: columnList
                });
            }
            await setOrderList(result);
            href = result;
            download = `${t("reportFilenames.orderList", {ns: "reportPage"})} - ${moment(
                Date()
            ).format("DD-MM-YYYY-HH-mm-ss")}.xlsx`;
        }
        return {href, download};
    };
    //#endregion

    const handleReportDownload = () => {
        onClickOkButton().then(({href, download}) => {
            const link = document.createElement("a");
            link.href = href;
            link.download = download;
            link.click();
            setDownloadReport(true);
        });
    };

    const options = [
        {label: t("fields.courierType", {ns: "common"}), value: "courierType"},
        {label: t("fields.city", {ns: "common"}), value: "city"}
    ];
    const dateOptions = [
        {label: t("dateOptions.total", {ns: "reportPage"}), value: undefined},
        {label: t("dateOptions.day", {ns: "reportPage"}), value: "dateAsDays"},
        {label: t("dateOptions.dayHour", {ns: "reportPage"}), value: "dateAsHours"}
    ];
    //#region renders
    const renderCompanyFields = useMemo(() => {
        if (!["deliveryOrders"].includes(reportType)) {
            return null;
        }
        return (
            <Col span={24} style={{marginBottom: "-20px"}}>
                <Row gutter={[20, 0]}>
                    <Col xs={12} sm={8} md={8} lg={4} xl={3}>
                        <CityFilter
                            showAllOption
                            value={selectedCity}
                            onChange={value => {
                                setSelectedCity(value);
                            }}
                        />
                    </Col>
                    {workType === "pool" ? (
                        <Col xs={24} sm={8} md={8} lg={4} xl={3}>
                            <h5>{t("fields.poolRegion", {ns: "pools"})}</h5>
                            <SelectField
                                mode="multiple"
                                data-testid="poolGroupSelect"
                                value={poolGroups}
                                placeholder={t("filters.addFilter", {ns: "common"})}
                                onChange={value => setPoolGroups(value)}
                                allowClear
                            >
                                {pools?.data
                                    ? pools?.data.map(pool => (
                                          <>
                                              <SelectField.Option
                                                  key={pool?._id}
                                                  value={pool?._id}
                                                  data-testid="poolGroups_option"
                                              >
                                                  {pool?.name}
                                              </SelectField.Option>
                                          </>
                                      ))
                                    : ""}
                            </SelectField>
                        </Col>
                    ) : null}
                    <Col span="8">
                        <CompanyFilter
                            mode="multiple"
                            showPoolOption={false}
                            allKey="all"
                            value={selectedCompanies}
                            onChange={value => {
                                setSelectedCompanies(value);
                                setSelectedSubCompanies([]);
                            }}
                        />
                    </Col>
                    <Col span="8">
                        <SubCompanyFilter
                            mode="multiple"
                            showAllOption={false}
                            value={selectedSubCompanies}
                            onChange={setSelectedSubCompanies}
                            companies={selectedCompanies}
                            disabled={!Boolean(selectedCompanies.length)}
                        />
                    </Col>
                </Row>
            </Col>
        );
    }, [
        reportType,
        selectedCompanies,
        selectedSubCompanies,
        workType,
        poolGroups,
        selectedCity,
        pools
    ]);

    const renderReportColumns = useMemo(() => {
        if (Boolean(reportColumns)) {
            const checkboxList = [];
            for (let column of reportColumns) {
                checkboxList.push({
                    label: t(`reportTypes.deliveries.${column.key}`, {ns: "reportPage"}),
                    value: column.key
                });
            }

            return (
                <Col span={24}>
                    <Row gutter={[20, 0]}>
                        <Col span="16">
                            <h5>{t("fieldsToAdd", {ns: "reportPage"})}</h5>
                            <SelectField
                                mode="multiple"
                                showSearch
                                allowClear
                                value={selectedColumns}
                                data-testid="columns"
                                onChange={setSelectedColumns}
                            >
                                {arrayAlphabeticalSort([...checkboxList], "label").map(
                                    (column, i) => (
                                        <SelectField.Option
                                            data-testid="columns_option"
                                            key={i}
                                            value={column.value}
                                        >
                                            {column.label}
                                        </SelectField.Option>
                                    )
                                )}
                            </SelectField>
                        </Col>
                    </Row>
                </Col>
            );
        } else {
            return null;
        }
    }, [reportColumns, selectedColumns, setSelectedColumns, i18n?.resolvedLanguage]);

    return useObserver(() => (
        <>
            <Row gutter={[0, 20]} align="bottom" data-testid="raporScreen">
                <Col span={24}>
                    <Col xs={24} sm={8} md={8} lg={6} xl={4}>
                        <h5>{t("reportSubject", {ns: "reportPage"})}</h5>
                        <SelectField
                            value={reportType}
                            data-testid="raporSelector"
                            onChange={onChangeReportType}
                        >
                            <SelectField.Option value="">
                                {t("choose", {ns: "reportPage"})}
                            </SelectField.Option>
                            {stores.meStore.checkRole("isAdmin") && (
                                <>
                                    <SelectField.Option
                                        value="userReport"
                                        data-testid="raporSelector_option"
                                        data-testvalue="userReport"
                                    >
                                        Kullanıcı Raporu
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value="shiftEditLogs"
                                        data-testid="raporSelector_option"
                                        data-testvalue="shiftEditLogs"
                                    >
                                        <>
                                            Vardiya Güncelleme <br />
                                            Kayıtları Raporu
                                        </>
                                    </SelectField.Option>
                                </>
                            )}
                            {stores.meStore.checkRole("isAdmin") && (
                                <SelectField.Option
                                    value="poolShiftsReport"
                                    data-testid="raporSelector_option"
                                    data-testvalue="poolShiftsReport"
                                >
                                    Havuz Vardiya Raporu
                                </SelectField.Option>
                            )}
                            {stores.meStore.checkRole("isAdmin") && (
                                <SelectField.Option
                                    value="subcompanyContracts"
                                    data-testid="raporSelector_option"
                                    data-testvalue="subcompanyContracts"
                                >
                                    Alt Firma Kontratları
                                </SelectField.Option>
                            )}
                            {stores.meStore.checkRole("notVisibleForCompanyUser") ? (
                                <>
                                    <SelectField.Option
                                        value="hours"
                                        data-testid="raporSelector_option"
                                        data-testvalue="hours"
                                    >
                                        {t("hoursWorked", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value="shifts"
                                        data-testid="raporSelector_option"
                                        data-testvalue="shifts"
                                    >
                                        {t("shiftsScore", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value="dailyShifts"
                                        data-testid="raporSelector_option"
                                        data-testvalue="dailyShifts"
                                    >
                                        {t("shiftsScoreDaily", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value="feedbacks"
                                        data-testid="raporSelector_option"
                                        data-testvalue="feedbacks"
                                    >
                                        {t("courierFeedbacks", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value="dedications"
                                        data-testid="raporSelector_option"
                                        data-testvalue="dedications"
                                    >
                                        {t("courierAssignments", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value="deliveryOrders"
                                        data-testid="raporSelector_option"
                                        data-testvalue="deliveryOrders"
                                    >
                                        {t("orderReport", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value="subCompanies"
                                        data-testid="raporSelector_option"
                                        data-testvalue="subCompanies"
                                    >
                                        {t("subCompanies", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value="subcompanySales"
                                        data-testid="raporSelector_option"
                                        data-testvalue="subcompanySales"
                                    >
                                        {"Alt Firma Satış Raporu"}
                                    </SelectField.Option>
                                </>
                            ) : (
                                <>
                                    <SelectField.Option
                                        value="deliveryOrders"
                                        data-testid="raporSelector_option"
                                        data-testvalue="deliveryOrders"
                                    >
                                        {t("orderReport", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value="shifts"
                                        data-testid="raporSelector_option"
                                        data-testvalue="shifts"
                                    >
                                        {t("shiftsScore", {ns: "reportPage"})}
                                    </SelectField.Option>
                                </>
                            )}
                            {stores.meStore.checkRole("isSOperationUser") ? (
                                <>
                                    <SelectField.Option
                                        value="dailyShifts"
                                        data-testid="raporSelector_option"
                                        data-testvalue="dailyShifts"
                                    >
                                        {t("shiftsScoreDaily", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value="subCompanies"
                                        data-testid="raporSelector_option"
                                        data-testvalue="subCompanies"
                                    >
                                        {t("subCompanies", {ns: "reportPage"})}
                                    </SelectField.Option>
                                </>
                            ) : (
                                <></>
                            )}
                        </SelectField>
                    </Col>
                </Col>
                {reportType === "poolShiftsReport" && (
                    <Col xs={24} sm={8} md={8} lg={4} xl={3}>
                        <h5>{t("fields.poolRegion", {ns: "pools"})}</h5>
                        <SelectField
                            mode="multiple"
                            data-testid="poolGroupSelect"
                            value={poolIdsShifts}
                            placeholder={t("filters.addFilter", {ns: "common"})}
                            onChange={value => setPoolIdsShifts(value)}
                            allowClear
                            showSearch
                            filterOption={(input, option) =>
                                normalizeText(option.props.children).indexOf(
                                    normalizeText(input)
                                ) >= 0
                            }
                            optionFilterProp="children"
                        >
                            {pools?.data
                                ? pools?.data.map(pool => (
                                      <>
                                          <SelectField.Option
                                              key={pool?._id}
                                              value={pool?._id}
                                              data-testid="poolGroups_option"
                                          >
                                              {pool?.name}
                                          </SelectField.Option>
                                      </>
                                  ))
                                : ""}
                        </SelectField>
                    </Col>
                )}
                {reportType === "subcompanyContracts" && (
                    <>
                        <Col xs={24} sm={8} md={8} lg={4} xl={3} style={{marginRight: "10px"}}>
                            <h5>Alt Firma Aktifliği</h5>
                            <SelectField value={activeSubCompany} onChange={setActiveSubCompany}>
                                <SelectField.Option key={"all"} value={"all"}>
                                    Tümü
                                </SelectField.Option>
                                <SelectField.Option key={"true"} value={true}>
                                    Aktif
                                </SelectField.Option>
                                <SelectField.Option key={"false"} value={false}>
                                    Pasif
                                </SelectField.Option>
                            </SelectField>
                        </Col>
                        <Col xs={24} sm={8} md={8} lg={4} xl={3} style={{marginRight: "10px"}}>
                            <h5>Tarih</h5>
                            <DateRangePicker
                                locale={locale}
                                format={"DD/MM/YYYY"}
                                onChange={date => {
                                    setContractDate(date);
                                }}
                            />
                        </Col>
                    </>
                )}
                {["deliveryOrders", "subcompanyContracts"].includes(reportType) && (
                    <Col xs={24} sm={8} md={8} lg={4} xl={3}>
                        <h5>
                            {reportType === "deliveryOrders"
                                ? "Sipariş türleri"
                                : "Alt Firma Çalışma Şekli"}
                        </h5>
                        <SelectField
                            data-testid="workType"
                            value={workType}
                            placeholder={t("filters.addFilter", {ns: "common"})}
                            onChange={value => setWorkType(value)}
                            allowClear
                        >
                            <SelectField.Option key={"all"} value={"all"}>
                                Tümü
                            </SelectField.Option>
                            <SelectField.Option key={"pool"} value={"pool"}>
                                Havuz
                            </SelectField.Option>
                            <SelectField.Option key={"dedicated"} value={"dedicated"}>
                                Sabit
                            </SelectField.Option>
                        </SelectField>
                    </Col>
                )}
                {reportType &&
                    [
                        "dedications",
                        "dailyShifts",
                        "subCompanies",
                        "userReport",
                        "subcompanySales",
                        "subcompanyContracts"
                    ].indexOf(reportType) === -1 && (
                        <>
                            <Col span={24}>
                                <Row gutter={[20, 20]}>
                                    <Col xs={12} sm={8} md={8} lg={4} xl={5}>
                                        <h5>{t("fields.startDate", {ns: "common"})}</h5>
                                        <DateRangePicker
                                            locale={locale}
                                            onChange={dates => {
                                                const updatedDate = [...dateRange];
                                                updatedDate[0] = dates;
                                                setDateRange(updatedDate);
                                            }}
                                            format={"DD/MM/YYYY"}
                                            placeholder={t("fields.startDate", {ns: "common"})}
                                            data-testid="startTime"
                                            value={dateRange[0] ? moment(dateRange[0]) : null}
                                        />
                                    </Col>
                                    <Col xs={12} sm={8} md={8} lg={4} xl={5}>
                                        <h5>{t("fields.endDate", {ns: "common"})}</h5>
                                        <DateRangePicker
                                            locale={locale}
                                            onChange={dates => {
                                                const updatedDate = [...dateRange];
                                                updatedDate[1] = dates;
                                                setDateRange(updatedDate);
                                            }}
                                            format={"DD/MM/YYYY"}
                                            placeholder={t("fields.endDate", {ns: "common"})}
                                            data-testid="endTime"
                                            value={dateRange[1] ? moment(dateRange[1]) : null}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                            {renderCompanyFields}
                            {renderReportColumns}
                        </>
                    )}
                {reportType === "hours" && (
                    <Col span={24}>
                        <Row gutter={[20, 20]}>
                            <Col xs={24} sm={8} md={8} lg={4} xl={5}>
                                <h5>{t("columnsToAdd", {ns: "reportPage"})}</h5>
                                <Checkbox.Group
                                    options={options}
                                    value={additionalColumns}
                                    data-test
                                    data-testid="additionalColumns"
                                    onChange={value => {
                                        setAdditionalColumns(value);
                                    }}
                                />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={4} xl={5}>
                                <h5>{t("filterBy", {ns: "reportPage"})}</h5>
                                <Radio.Group
                                    options={dateOptions}
                                    value={dateOption}
                                    data-testid="dateOptions"
                                    onChange={e => {
                                        setDateOption(e.target.value);
                                    }}
                                />
                            </Col>
                        </Row>
                    </Col>
                )}
                {reportType === "subcompanySales" && (
                    <Col span={24}>
                        <Row gutter={[20, 20]}>
                            <Col xs={12} sm={8} md={8} lg={4} xl={3}>
                                <CityFilter
                                    showAllOption
                                    value={selectedCity}
                                    onChange={value => {
                                        setSelectedCity(value);
                                    }}
                                />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={4} xl={3}>
                                <h5>Sipariş türleri</h5>
                                <SelectField
                                    data-testid="workType"
                                    value={workType}
                                    placeholder={t("filters.addFilter", {ns: "common"})}
                                    onChange={value => setWorkType(value)}
                                    allowClear
                                >
                                    <SelectField.Option key={"all"} value={"all"}>
                                        Tümü
                                    </SelectField.Option>
                                    <SelectField.Option key={"pool"} value={"pool"}>
                                        Havuz
                                    </SelectField.Option>
                                    <SelectField.Option key={"dedicated"} value={"dedicated"}>
                                        Sabit
                                    </SelectField.Option>
                                </SelectField>
                            </Col>
                            <Col span="8">
                                <CompanyFilter
                                    showPoolOption={false}
                                    allKey="all"
                                    value={selectedCompany}
                                    onChange={value => {
                                        setSelectedCompany(value);
                                        setSelectedSubCompanies([]);
                                    }}
                                />
                            </Col>
                        </Row>
                    </Col>
                )}
            </Row>
            <Row style={{marginTop: "20px"}}>
                {reportType && (
                    <Tooltip title={t("actions.downloadReport", {ns: "reportPage"})}>
                        <DownloadButton
                            type="primary"
                            icon={<DownloadOutlined spin={stores.appStore.loading} />}
                            data-testid="downloadButton"
                            onClick={handleReportDownload}
                        />
                    </Tooltip>
                )}
            </Row>
        </>
    ));
    //#endregion
};
